import { useEffect, useCallback, useRef } from 'react';
import { store } from '../redux/store';
import { updateUserDefaultFacility } from '../redux/slices/authSlice';

// Global state to track active listeners and prevent cascade effects
let globalListenerCount = 0;
let globalDebounceTimer = null;
let lastProcessedFacilityId = null;
let lastProcessedTimestamp = 0;

/**
 * Custom hook to listen for default facility changes and trigger appropriate refreshes
 * @param {Function} onFacilityChange - Callback function to execute when facility changes
 * @param {Boolean} disabled - Whether to disable the facility change listener (default: false)
 */
export const useFacilityChangeListener = (onFacilityChange = null, disabled = false) => {
  const listenerIdRef = useRef(null);
  const onFacilityChangeRef = useRef(onFacilityChange);
  const lastDisabledStateRef = useRef(disabled);
  const mountedRef = useRef(false);

  // Assign unique ID to this listener instance
  useEffect(() => {
    if (!listenerIdRef.current) {
      listenerIdRef.current = `listener_${++globalListenerCount}`;
    }
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Update the callback ref when the callback changes
  useEffect(() => {
    onFacilityChangeRef.current = onFacilityChange;
  }, [onFacilityChange]);

  // Track when disabled state changes
  useEffect(() => {
    if (lastDisabledStateRef.current !== disabled) {
      lastDisabledStateRef.current = disabled;
    }
  }, [disabled]);

  // Stabilized event handler with enhanced debouncing
  const handleFacilityChange = useCallback((event) => {
    // Skip if disabled or component is unmounted
    if (disabled || !mountedRef.current) {
      return;
    }

    const { facilityId, previousFacility } = event.detail;
    const currentTimestamp = Date.now();

    // Enhanced deduplication - check if this is the same facility within a short time window
    if (lastProcessedFacilityId === facilityId && 
        (currentTimestamp - lastProcessedTimestamp) < 500) {
      return;
    }

    // Clear any existing global debounce timer
    if (globalDebounceTimer) {
      clearTimeout(globalDebounceTimer);
    }

    // Global debounce to prevent cascade effects from multiple listeners
    globalDebounceTimer = setTimeout(() => {
      // Check if component is still mounted before executing callback
      if (!mountedRef.current) {
        return;
      }

      // Update global state
      lastProcessedFacilityId = facilityId;
      lastProcessedTimestamp = currentTimestamp;

      // Call the custom callback if provided
      if (onFacilityChangeRef.current) {
        try {
          onFacilityChangeRef.current(facilityId, previousFacility);
        } catch (error) {
          console.error('useFacilityChangeListener: Error in callback:', error);
        }
      }
      
      // Update the auth state to reflect the new default facility
      // This ensures all components using the auth state get the updated facility
      try {
        // Find the complete facility object from the store
        const state = store.getState();
        const userFacilities = state.facilities.userFacilities;
        const facilityObject = userFacilities.find(f => f.id === facilityId);
        
        if (facilityObject) {
          store.dispatch(updateUserDefaultFacility(facilityObject));
        }
      } catch (error) {
        console.error('useFacilityChangeListener: Error updating auth state:', error);
      }
    }, 150); // Increased debounce time to prevent rapid-fire executions
  }, [disabled]);

  // Set up event listener with proper cleanup
  useEffect(() => {
    // Don't add event listener if disabled
    if (disabled) {
      return;
    }

    // Listen for facility change events
    window.addEventListener('defaultFacilityChanged', handleFacilityChange);
    
    return () => {
      window.removeEventListener('defaultFacilityChanged', handleFacilityChange);
      
      // Clean up global timer if this was the component that set it
      if (globalDebounceTimer) {
        clearTimeout(globalDebounceTimer);
        globalDebounceTimer = null;
      }
    };
  }, [handleFacilityChange, disabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clean up global timer if this was the component that set it
      if (globalDebounceTimer) {
        clearTimeout(globalDebounceTimer);
        globalDebounceTimer = null;
      }
    };
  }, []);
}; 